import { StateCreator } from 'zustand';

export interface OrderManagerFilters {
  patientSearch: string;
  clinicId: string | null;
  hideTest: boolean;
  hideImportedPatients: boolean;
  /** Exclude orders dated before the patient's confirmed take-over day (Order.order_date). */
  excludePreTakeover: boolean;
  orderType: string | null;
  clinicPhysicianId: string | null;
  unassignedPhysician: boolean;
  orderDateFrom: string;
  orderDateTo: string;
}

export interface OrderManagerSlice {
  orderManagerFilters: OrderManagerFilters;
  setOrderManagerFilters: (filters: Partial<OrderManagerFilters>) => void;
}

export const DEFAULT_ORDER_MANAGER_FILTERS: OrderManagerFilters = {
  patientSearch: '',
  clinicId: null,
  hideTest: true,
  hideImportedPatients: true,
  // Default on for dual-config agencies; control is hidden otherwise.
  excludePreTakeover: true,
  orderType: null,
  clinicPhysicianId: null,
  unassignedPhysician: false,
  orderDateFrom: '',
  orderDateTo: '',
};

export const orderManagerSlice: StateCreator<OrderManagerSlice> = set => ({
  orderManagerFilters: DEFAULT_ORDER_MANAGER_FILTERS,
  setOrderManagerFilters: (filters: Partial<OrderManagerFilters>) =>
    set(state => ({
      orderManagerFilters: { ...state.orderManagerFilters, ...filters },
    })),
});

/** Both ends set and From ≤ To; otherwise the range is not applied. */
export function orderManagerAppliedDateRange(
  filters: OrderManagerFilters
): { from: string; to: string } | null {
  const from = filters.orderDateFrom;
  const to = filters.orderDateTo;
  if (!from || !to || to < from) return null;
  return { from, to };
}

export function orderManagerDateRangeInvalid(
  filters: OrderManagerFilters
): boolean {
  return Boolean(
    filters.orderDateFrom &&
    filters.orderDateTo &&
    filters.orderDateTo < filters.orderDateFrom
  );
}

/** True when any narrowing filter is set (for empty-state copy). */
export function orderManagerHasNarrowingFilters(
  filters: OrderManagerFilters
): boolean {
  return Boolean(
    filters.patientSearch.trim() ||
    filters.clinicId ||
    filters.orderType ||
    filters.clinicPhysicianId ||
    filters.unassignedPhysician ||
    orderManagerAppliedDateRange(filters)
  );
}

/** Query params shared by the list and the tab-count endpoints. */
export function orderManagerFilterQuery(filters: OrderManagerFilters) {
  const dateRange = orderManagerAppliedDateRange(filters);
  return {
    hide_imported_patients: filters.hideImportedPatients,
    hide_test: filters.hideTest,
    exclude_pre_takeover: filters.excludePreTakeover !== false,
    patient_search: filters.patientSearch.trim() || undefined,
    clinic_id: filters.clinicId || undefined,
    order_type: filters.orderType || undefined,
    clinic_physician_id: filters.unassignedPhysician
      ? undefined
      : filters.clinicPhysicianId || undefined,
    unassigned_physician: filters.unassignedPhysician || undefined,
    order_date_from: dateRange?.from,
    order_date_to: dateRange?.to,
  };
}

/** Remount key so a filter flip starts each tab at page 1. */
export function orderManagerFilterRemountKey(
  filters: OrderManagerFilters
): string {
  const query = orderManagerFilterQuery(filters);
  return [
    query.hide_imported_patients ? 'hide' : 'show',
    query.hide_test ? 'hide-test' : 'show-test',
    query.exclude_pre_takeover ? 'exclude-pre' : 'include-pre',
    query.patient_search || '-',
    query.clinic_id ?? 'all-clinics',
    query.order_type ?? 'all-types',
    query.unassigned_physician
      ? 'unassigned'
      : (query.clinic_physician_id ?? 'all-phys'),
    query.order_date_from || '-',
    query.order_date_to || '-',
  ].join('|');
}
